import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Combobox } from "@/components/combobox";
import type { ComboOption } from "@/components/combobox";
import Textarea from "@/components/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { captureEvent } from "@/lib/analytics/posthog";
import { CANCEL_REASONS } from "@/queries/api/workerDashboardApi";
import type {
  CancelReason,
  WorkerUpcomingShift,
} from "@/queries/api/workerDashboardApi";
import {
  useCancelShiftMutation,
  useRunningLateMutation,
} from "@/queries/hooks/workerDashboardHooks";
import { isApiError } from "@/queries/api/apiUtils";

type Step = "choice" | "late" | "cancel";

/** Sentinel the backend renders as "over an hour late" (anything > 60 min). */
const OVER_AN_HOUR_MINUTES = 61;

const ETA_OPTIONS: Array<{ value: number; key: string }> = [
  { value: 15, key: "min15" },
  { value: 30, key: "min30" },
  { value: 60, key: "hour1" },
  { value: OVER_AN_HOUR_MINUTES, key: "overHour" },
];

type Props = {
  shift: WorkerUpcomingShift;
  open: boolean;
  onOpenChange: (o: boolean) => void;
};

function errMessage(err: unknown, fallback: string): string {
  if (isApiError(err)) return err.message;
  return fallback;
}

export function ChangeCancelDialog({ shift, open, onOpenChange }: Props) {
  const { t } = useTranslation("dashboard");
  const cancel = useCancelShiftMutation();
  const late = useRunningLateMutation();

  const [step, setStep] = useState<Step>("choice");
  const [eta, setEta] = useState<number | null>(null);
  const [lateNote, setLateNote] = useState("");
  const [reason, setReason] = useState<CancelReason | null>(null);
  const [cancelNote, setCancelNote] = useState("");

  // Reset all local state whenever the dialog is closed.
  useEffect(() => {
    if (!open) {
      setStep("choice");
      setEta(null);
      setLateNote("");
      setReason(null);
      setCancelNote("");
      cancel.reset();
      late.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const reasonOptions: Array<ComboOption> = CANCEL_REASONS.map((r) => ({
    value: r,
    label: t(`workerV2.upcoming.changeCancel.reasons.${r}`),
  }));

  const etaOptions: Array<ComboOption> = ETA_OPTIONS.map((o) => ({
    value: String(o.value),
    label: t(`workerV2.upcoming.changeCancel.etaOptions.${o.key}`),
  }));

  const noteRequired = reason === "OTHER";
  const cancelDisabled =
    !reason || (noteRequired && cancelNote.trim().length === 0) || cancel.isPending;

  const onSubmitLate = () => {
    const etaMinutes = eta ?? undefined;
    captureEvent("upcoming_action", {
      status: shift.status,
      action: "running_late_submit",
    });
    late.mutate(
      { shiftId: shift.shift_id, etaMinutes, note: lateNote.trim() || undefined },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  const onSubmitCancel = () => {
    if (!reason) return;
    captureEvent("upcoming_action", {
      status: shift.status,
      action: "cancel_submit",
      reason,
    });
    cancel.mutate(
      { shiftId: shift.shift_id, reason, note: cancelNote.trim() || undefined },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white">
        <DialogHeader>
          <DialogTitle className="text-garnet">
            {t("workerV2.upcoming.changeCancel.title")}
          </DialogTitle>
        </DialogHeader>

        {step === "choice" ? (
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => {
                captureEvent("upcoming_action", {
                  status: shift.status,
                  action: "running_late",
                });
                setStep("late");
              }}
              className="h-11 px-3 rounded-lg border border-fog-dark text-sm font-semibold text-garnet hover:bg-fog"
            >
              {t("workerV2.upcoming.changeCancel.runningLate")}
            </button>
            <button
              type="button"
              onClick={() => {
                captureEvent("upcoming_action", {
                  status: shift.status,
                  action: "cant_make_it",
                });
                setStep("cancel");
              }}
              className="h-11 px-3 rounded-lg border border-warn-border text-sm font-semibold text-warn-deep hover:bg-warn-soft/40"
            >
              {t("workerV2.upcoming.changeCancel.cantMakeIt")}
            </button>
          </div>
        ) : null}

        {step === "late" ? (
          <div className="flex flex-col gap-3">
            <Combobox
              label={t("workerV2.upcoming.changeCancel.etaLabel")}
              value={eta == null ? null : String(eta)}
              onChange={(v) => setEta(v == null ? null : Number(v))}
              options={etaOptions}
            />
            <Textarea
              value={lateNote}
              onChange={(e) => setLateNote(e.currentTarget.value)}
              label={t("workerV2.upcoming.changeCancel.noteLabel")}
            />
            {late.isError ? (
              <p className="text-xs text-red-500">
                {errMessage(late.error, t("workerV2.upcoming.changeCancel.error"))}
              </p>
            ) : null}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setStep("choice")}
                className="h-9 px-3 rounded-lg border border-fog-dark text-xs font-semibold text-garnet hover:bg-fog"
              >
                {t("workerV2.upcoming.changeCancel.back")}
              </button>
              <button
                type="button"
                disabled={late.isPending}
                onClick={onSubmitLate}
                className="h-9 px-4 rounded-lg bg-garnet text-white text-xs font-semibold disabled:opacity-60"
              >
                {t("workerV2.upcoming.changeCancel.send")}
              </button>
            </div>
          </div>
        ) : null}

        {step === "cancel" ? (
          <div className="flex flex-col gap-3">
            <Combobox
              label={t("workerV2.upcoming.changeCancel.reasonLabel")}
              value={reason}
              onChange={(v) => setReason(v as CancelReason | null)}
              options={reasonOptions}
              required
            />
            <Textarea
              value={cancelNote}
              onChange={(e) => setCancelNote(e.currentTarget.value)}
              label={
                noteRequired
                  ? t("workerV2.upcoming.changeCancel.noteRequired")
                  : t("workerV2.upcoming.changeCancel.noteLabel")
              }
              state={noteRequired && cancelNote.trim().length === 0 ? "error" : "default"}
            />

            <div className="rounded-md bg-warn-tint border border-warn-border px-3 py-2">
              <p className="text-xs text-warn-deep">
                {t("workerV2.upcoming.changeCancel.ratingWarning")}
              </p>
            </div>

            {cancel.isError ? (
              <p className="text-xs text-red-500">
                {errMessage(cancel.error, t("workerV2.upcoming.changeCancel.error"))}
              </p>
            ) : null}

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setStep("choice")}
                className="h-9 px-3 rounded-lg border border-fog-dark text-xs font-semibold text-garnet hover:bg-fog"
              >
                {t("workerV2.upcoming.changeCancel.back")}
              </button>
              <button
                type="button"
                disabled={cancelDisabled}
                onClick={onSubmitCancel}
                className="h-9 px-4 rounded-lg bg-warn text-white text-xs font-semibold disabled:opacity-60"
              >
                {t("workerV2.upcoming.changeCancel.confirmCancel")}
              </button>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
